import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  MenuItem,
  Checkbox,
  ListItemIcon,
  Menu,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  LocalHospital,
  TipsAndUpdates,
  Close,
  Add,
  ArrowForwardIos,
  Info,
  FilterList,
  ExpandMore,
} from '@mui/icons-material';
import { RouterLink as Link } from '../common/RouterLink';
import {
  useCases,
  type DiagnosisResult,
} from '../../contexts/api-contexts/CasesContext';
import { useSymptoms } from '../../contexts/api-contexts/SymptomsContext';
import CaseDiagnosisInfoDialog from './CaseDiagnosisInfoDialog';

interface CaseDiagnosesProps {
  onNavigate?: (path: string) => void;
  onTabChange?: (
    tab: 'basic' | 'symptoms' | 'diagnoses' | 'treatments'
  ) => void;
}

const CaseDiagnoses: React.FC<CaseDiagnosesProps> = ({
  onNavigate: _onNavigate,
  onTabChange,
}) => {
  const { currentCase, setCaseProperty, persistCase } = useCases();
  const { ensureSymptoms, symptoms } = useSymptoms();

  // State for diagnosis info dialog
  const [diagnosisInfoOpen, setDiagnosisInfoOpen] = useState(false);
  const [selectedDiagnosisForInfo, setSelectedDiagnosisForInfo] =
    useState<DiagnosisResult | null>(null);

  // State for filters
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [significantSymptomsMenuAnchor, setSignificantSymptomsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [significantSymptomsFilter, setSignificantSymptomsFilter] = useState<
    number[]
  >([]);

  // Navigation handler
  // Filter menu handlers
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleSignificantSymptomsMenuOpen = (
    event: React.MouseEvent<HTMLElement>
  ): void => {
    setSignificantSymptomsMenuAnchor(event.currentTarget);
  };

  const handleSignificantSymptomsMenuClose = () => {
    setSignificantSymptomsMenuAnchor(null);
  };

  const handleFilterSelect = (filterType: string) => {
    if (activeFilter === filterType) {
      // Toggle off if already active
      setActiveFilter(null);
      setSignificantSymptomsFilter([]);
    } else {
      setActiveFilter(filterType);
      if (filterType === 'significant-symptoms') {
        // Initialize with symptoms that are actually marked as significant in the case
        const significantSymptomIds =
          currentCase?.selected_symptoms
            ?.filter((s) => s.significant)
            .map((s) => s.id) || [];
        setSignificantSymptomsFilter(significantSymptomIds);
      }
    }
    handleFilterMenuClose();
  };

  const handleSignificantSymptomToggle = async (symptomId: number) => {
    // Update the actual significant status of the symptom in the case
    if (!currentCase) return;

    const updatedSelectedSymptoms =
      currentCase.selected_symptoms?.map((symptom) =>
        symptom.id === symptomId
          ? { ...symptom, significant: !symptom.significant }
          : symptom
      ) || [];

    setCaseProperty('selected_symptoms', updatedSelectedSymptoms);

    // Update the local filter state to reflect the new significant status
    const symptom = currentCase.selected_symptoms?.find(
      (s) => s.id === symptomId
    );
    if (symptom) {
      const isBecomingSignificant = !symptom.significant;
      setSignificantSymptomsFilter((prev) =>
        isBecomingSignificant
          ? [...prev, symptomId]
          : prev.filter((id) => id !== symptomId)
      );
    }

    // Persist the case after updating significant symptoms
    try {
      await persistCase();
    } catch (error) {
      console.error(
        'Error persisting case after toggling significant symptom:',
        error
      );
    }
  };

  // Handle diagnosis deselection
  const handleDeselectDiagnosis = useCallback(
    async (diagnosisId: number) => {
      if (!currentCase) return;

      const updatedSelectedDiagnoses =
        currentCase.selected_diagnoses?.filter(
          (diagnosis) => diagnosis.id !== diagnosisId
        ) || [];

      setCaseProperty('selected_diagnoses', updatedSelectedDiagnoses);

      // Persist the case after updating diagnoses
      try {
        await persistCase();
      } catch (error) {
        console.error(
          'Error persisting case after deselecting diagnosis:',
          error
        );
      }
    },
    [currentCase, setCaseProperty, persistCase]
  );

  // Handle adding diagnosis to selected list
  const handleAddDiagnosis = useCallback(
    async (diagnosis: DiagnosisResult) => {
      if (!currentCase) return;

      const updatedSelectedDiagnoses = [
        ...(currentCase.selected_diagnoses || []),
        diagnosis,
      ];

      setCaseProperty('selected_diagnoses', updatedSelectedDiagnoses);

      // Persist the case after updating diagnoses
      try {
        await persistCase();
      } catch (error) {
        console.error('Error persisting case after adding diagnosis:', error);
      }
    },
    [currentCase, setCaseProperty, persistCase]
  );

  // Handle opening diagnosis info dialog
  const handleOpenDiagnosisInfo = useCallback((diagnosis: DiagnosisResult) => {
    setSelectedDiagnosisForInfo(diagnosis);
    setDiagnosisInfoOpen(true);
  }, []);

  // Convert selected diagnoses to display format
  const displaySelectedDiagnoses = useMemo(() => {
    return currentCase?.selected_diagnoses || [];
  }, [currentCase?.selected_diagnoses]);

  // Convert suggested diagnoses to display format (including already selected with indicator)
  const displaySuggestedDiagnoses = useMemo(() => {
    const selectedDiagnosisIds = new Set(
      currentCase?.selected_diagnoses?.map((d) => d.id) || []
    );

    const diagnosesWithSelection =
      currentCase?.diagnoses?.map((diagnosis) => ({
        ...diagnosis,
        isSelected: selectedDiagnosisIds.has(diagnosis.id),
      })) || [];

    // Apply significant symptoms filter if active
    if (
      activeFilter === 'significant-symptoms' &&
      significantSymptomsFilter.length > 0
    ) {
      // For now, we'll show all diagnoses since the filtering logic would need
      // to be implemented based on the diagnosis-symptom relationship
      // This is a placeholder for the actual filtering logic
      // TODO: Implement actual filtering based on significant symptoms
    }

    return diagnosesWithSelection;
  }, [
    currentCase?.diagnoses,
    currentCase?.selected_diagnoses,
    activeFilter,
    significantSymptomsFilter,
  ]);

  // Helper function to format count with 99+ limit
  const formatCount = (count: number): string => {
    return count >= 99 ? '99+' : count.toString();
  };

  // Get selected symptoms for filtering
  const selectedSymptomsForFilter = useMemo(() => {
    return currentCase?.selected_symptoms || [];
  }, [currentCase?.selected_symptoms]);

  // Helper function to get symptom name and type/category by ID
  const getSymptomDisplayById = useCallback(
    (symptomId: number): [string, string] => {
      const symptom = symptoms.find((s) => s.id === symptomId);
      if (!symptom) return [`Symptom ${symptomId}`, ''];

      let name = '';
      if (symptom.parent_symptom) {
        name = `${symptom.parent_symptom.name}, `;
      }
      name += symptom.name;

      let typeName = '';
      if (symptom.symptom_type) {
        typeName = `${symptom.symptom_type.category.name}, ${symptom.symptom_type.name}`;
      }

      return [name, typeName];
    },
    [symptoms]
  ); // Initialize significant symptoms filter from case data
  useEffect(() => {
    if (currentCase?.selected_symptoms) {
      const significantSymptomIds = currentCase.selected_symptoms
        .filter((s) => s.significant)
        .map((s) => s.id);

      // Only update if there are significant symptoms and the filter isn't already set
      if (
        significantSymptomIds.length > 0 &&
        significantSymptomsFilter.length === 0
      ) {
        setSignificantSymptomsFilter(significantSymptomIds);
      }
    }
  }, [currentCase?.selected_symptoms, significantSymptomsFilter.length]);

  // Ensure all selected symptoms are loaded for proper display
  useEffect(() => {
    if (
      currentCase?.selected_symptoms &&
      currentCase.selected_symptoms.length > 0
    ) {
      const allSymptomIds = currentCase.selected_symptoms.map((s) => s.id);
      ensureSymptoms(allSymptomIds);
    }
  }, [currentCase?.selected_symptoms, ensureSymptoms]);

  // Ensure symptoms are loaded when significant symptoms filter is active
  useEffect(() => {
    if (
      activeFilter === 'significant-symptoms' &&
      selectedSymptomsForFilter.length > 0
    ) {
      const symptomIds = selectedSymptomsForFilter.map((s) => s.id);
      ensureSymptoms(symptomIds);
    }
  }, [activeFilter, selectedSymptomsForFilter, ensureSymptoms]);

  return (
    <Card
      elevation={1}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <CardContent sx={{ flexGrow: 1, p: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocalHospital sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
            Case Diagnoses
          </Typography>
        </Box>

        {/* Selected Diagnoses Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
          >
            Selected Diagnoses ({displaySelectedDiagnoses.length})
          </Typography>
          {displaySelectedDiagnoses.length > 0 ? (
            <List
              dense
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {displaySelectedDiagnoses.map((diagnosis) => (
                <ListItem key={diagnosis.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={diagnosis.name}
                    secondary={`Symptom match score: ${Math.round(
                      diagnosis.probability
                    )}`}
                    primaryTypographyProps={{
                      variant: 'body1',
                    }}
                  />
                  <ListItemSecondaryAction
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {/* Info icon to show diagnosis details */}
                    <Tooltip title="Show diagnosis information">
                      <IconButton
                        edge="end"
                        aria-label="show info"
                        onClick={() => handleOpenDiagnosisInfo(diagnosis)}
                        size="small"
                        sx={{ color: 'info.main' }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      edge="end"
                      aria-label="deselect"
                      onClick={() => handleDeselectDiagnosis(diagnosis.id)}
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No diagnoses selected yet. Browse potential diagnoses below and
                add them to your selection.
              </Typography>
            </Box>
          )}

          {/* Navigation link to Treatments - always show but disabled when no diagnoses */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                displaySelectedDiagnoses.length === 0
                  ? 'Select at least one diagnosis to continue to treatments'
                  : 'Continue to treatment options'
              }
            >
              <span>
                <Link
                  to={`/cases/${currentCase?.id}?tab=treatments`}
                  style={{
                    textDecoration: 'none',
                    pointerEvents:
                      displaySelectedDiagnoses.length === 0 ? 'none' : 'auto',
                    opacity: displaySelectedDiagnoses.length === 0 ? 0.5 : 1,
                  }}
                  onClick={(e) => {
                    if (displaySelectedDiagnoses.length === 0) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    onTabChange?.('treatments');
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color:
                        displaySelectedDiagnoses.length === 0
                          ? 'text.disabled'
                          : 'primary.main',
                      fontSize: '1.1rem',
                      fontWeight: 'medium',
                      '&:hover': {
                        color:
                          displaySelectedDiagnoses.length === 0
                            ? 'text.disabled'
                            : 'primary.dark',
                      },
                    }}
                  >
                    Continue to Treatments
                    <ArrowForwardIos sx={{ fontSize: '1rem' }} />
                  </Box>
                </Link>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Potential Diagnoses Section */}
        {(displaySuggestedDiagnoses.length > 0 ||
          currentCase?.status === 'CALCULATING_DIAGNOSES') && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TipsAndUpdates
                sx={{ mr: 1, color: 'warning.main', fontSize: 18 }}
              />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                Potential Diagnoses (
                {currentCase?.status === 'CALCULATING_DIAGNOSES'
                  ? '...'
                  : formatCount(displaySuggestedDiagnoses.length)}
                )
              </Typography>
            </Box>

            {/* Show calculating status */}
            {currentCase?.status === 'CALCULATING_DIAGNOSES' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  backgroundColor: 'info.light',
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Calculating potential diagnoses based on selected symptoms...
                </Typography>
              </Box>
            )}

            {/* Filter Controls - Only show when not calculating */}
            {currentCase?.status !== 'CALCULATING_DIAGNOSES' &&
              displaySuggestedDiagnoses.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleFilterMenuOpen}
                    startIcon={<FilterList />}
                    endIcon={<ExpandMore />}
                    size="small"
                    sx={{ minWidth: 140 }}
                  >
                    Filter results
                  </Button>

                  <Menu
                    anchorEl={filterMenuAnchor}
                    open={Boolean(filterMenuAnchor)}
                    onClose={handleFilterMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem
                      onClick={() => handleFilterSelect('significant-symptoms')}
                      selected={activeFilter === 'significant-symptoms'}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={activeFilter === 'significant-symptoms'}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Significant symptoms" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleFilterSelect('geographical-areas')}
                      selected={activeFilter === 'geographical-areas'}
                      disabled
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={activeFilter === 'geographical-areas'}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Geographical areas"
                        secondary="Coming soon"
                      />
                    </MenuItem>
                  </Menu>

                  {/* Significant Symptoms Filter Dropdown */}
                  {(activeFilter === 'significant-symptoms' ||
                    significantSymptomsFilter.length > 0) && (
                    <Button
                      variant="outlined"
                      onClick={handleSignificantSymptomsMenuOpen}
                      endIcon={<ExpandMore />}
                      size="small"
                      color={
                        significantSymptomsFilter.length > 0
                          ? 'primary'
                          : 'inherit'
                      }
                      sx={{ minWidth: 200 }}
                    >
                      Significant symptoms ({significantSymptomsFilter.length})
                    </Button>
                  )}

                  <Menu
                    anchorEl={significantSymptomsMenuAnchor}
                    open={Boolean(significantSymptomsMenuAnchor)}
                    onClose={handleSignificantSymptomsMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    PaperProps={{
                      sx: { maxHeight: 300, minWidth: 250 },
                    }}
                  >
                    {selectedSymptomsForFilter.length === 0 ? (
                      <MenuItem disabled>
                        <ListItemText
                          primary="No symptoms selected yet"
                          secondary="Select symptoms first to use this filter"
                        />
                      </MenuItem>
                    ) : (
                      selectedSymptomsForFilter.map((symptom) => (
                        <MenuItem
                          key={symptom.id}
                          onClick={() =>
                            handleSignificantSymptomToggle(symptom.id)
                          }
                        >
                          <ListItemIcon>
                            <Checkbox checked={symptom.significant} />
                          </ListItemIcon>
                          <ListItemText
                            primary={getSymptomDisplayById(symptom.id)[0]}
                            secondary={getSymptomDisplayById(symptom.id)[1]}
                          />
                        </MenuItem>
                      ))
                    )}
                  </Menu>
                </Box>
              )}

            {/* Diagnoses List - Only show when not calculating and has diagnoses */}
            {currentCase?.status !== 'CALCULATING_DIAGNOSES' &&
              displaySuggestedDiagnoses.length > 0 && (
                <List
                  dense
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {displaySuggestedDiagnoses.map((diagnosis) => (
                    <ListItem
                      key={diagnosis.id}
                      sx={{
                        py: 0.5,
                        cursor: 'pointer',
                        bgcolor: diagnosis.isSelected
                          ? 'action.selected'
                          : 'inherit',
                        opacity: diagnosis.isSelected ? 0.7 : 1,
                        '&:hover': {
                          backgroundColor: diagnosis.isSelected
                            ? 'action.selected'
                            : 'action.hover',
                        },
                      }}
                      onClick={() => handleOpenDiagnosisInfo(diagnosis)}
                    >
                      <IconButton
                        edge="start"
                        aria-label="show info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDiagnosisInfo(diagnosis);
                        }}
                        size="small"
                        sx={{ color: 'info.main', mr: 1 }}
                      >
                        <Info fontSize="small" />
                      </IconButton>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {diagnosis.name}
                            {diagnosis.isSelected && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  bgcolor: 'success.light',
                                  color: 'success.contrastText',
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                }}
                              >
                                Selected
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={`Symptom match score: ${Math.round(
                          diagnosis.probability
                        )}`}
                        primaryTypographyProps={{
                          variant: 'body1',
                          component: 'div',
                        }}
                      />
                      <ListItemSecondaryAction>
                        {!diagnosis.isSelected && (
                          <IconButton
                            edge="end"
                            aria-label="add diagnosis"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddDiagnosis(diagnosis);
                            }}
                            size="small"
                            sx={{ color: 'success.main' }}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
          </Box>
        )}

        {/* No Diagnoses Message */}
        {displaySelectedDiagnoses.length === 0 &&
          displaySuggestedDiagnoses.length === 0 && (
            <Alert severity="info">
              No diagnosis data available for this case.
            </Alert>
          )}

        {/* Loading Indicator for CALCULATING_DIAGNOSES status */}
        {currentCase?.status === 'CALCULATING_DIAGNOSES' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              flexDirection: 'column',
            }}
          >
            <CircularProgress size={24} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Calculating potential diagnoses, please wait...
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Diagnosis Info Dialog */}
      <CaseDiagnosisInfoDialog
        open={diagnosisInfoOpen}
        onClose={() => setDiagnosisInfoOpen(false)}
        diagnosis={selectedDiagnosisForInfo}
        onAddToSelected={handleAddDiagnosis}
        isSelected={
          selectedDiagnosisForInfo
            ? displaySelectedDiagnoses.some(
                (d) => d.id === selectedDiagnosisForInfo.id
              )
            : false
        }
      />
    </Card>
  );
};

export default CaseDiagnoses;
